import * as React from "react"
import { createPortal } from "react-dom"
import { Check, ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"

// ─── Context ────────────────────────────────────────────────────────────────

interface ItemEntry { value: string; label: string }

interface SelectContextValue {
  open: boolean
  setOpen: (open: boolean) => void
  value: string | undefined
  onSelect: (value: string) => void
  triggerRef: React.RefObject<HTMLButtonElement | null>
  disabled: boolean
  items: ItemEntry[]
  registerItem: (value: string, label: string) => void
}

const SelectContext = React.createContext<SelectContextValue>({
  open: false,
  setOpen: () => {},
  value: undefined,
  onSelect: () => {},
  triggerRef: { current: null },
  disabled: false,
  items: [],
  registerItem: () => {},
})

// ─── Select (root) ──────────────────────────────────────────────────────────

interface SelectProps {
  children: React.ReactNode
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  disabled?: boolean
  name?: string
  required?: boolean
}

const Select: React.FC<SelectProps> = ({
  children,
  value: controlledValue,
  defaultValue,
  onValueChange,
  disabled = false,
}) => {
  const [open, setOpen] = React.useState(false)
  const [internalValue, setInternalValue] = React.useState<string | undefined>(defaultValue)
  const triggerRef = React.useRef<HTMLButtonElement | null>(null)
  const [items, setItems] = React.useState<ItemEntry[]>([])

  const value = controlledValue !== undefined ? controlledValue : internalValue

  const onSelect = React.useCallback((newValue: string) => {
    if (controlledValue === undefined) {
      setInternalValue(newValue)
    }
    onValueChange?.(newValue)
    setOpen(false)
    setTimeout(() => triggerRef.current?.focus(), 0)
  }, [controlledValue, onValueChange])

  const registerItem = React.useCallback((itemValue: string, label: string) => {
    setItems(prev => {
      const existing = prev.find(i => i.value === itemValue)
      if (existing?.label === label) return prev
      if (existing) return prev.map(i => i.value === itemValue ? { ...i, label } : i)
      return [...prev, { value: itemValue, label }]
    })
  }, [])

  const ctx = React.useMemo<SelectContextValue>(() => ({
    open, setOpen, value, onSelect, triggerRef, disabled, items, registerItem,
  }), [open, value, onSelect, disabled, items, registerItem])

  return (
    <SelectContext.Provider value={ctx}>
      {children}
    </SelectContext.Provider>
  )
}
Select.displayName = "Select"

// ─── SelectValue ────────────────────────────────────────────────────────────

interface SelectValueProps {
  children?: React.ReactNode
  placeholder?: React.ReactNode
  className?: string
}

const SelectValue = React.forwardRef<HTMLSpanElement, SelectValueProps>(
  ({ children, placeholder, className }, ref) => {
    const { value, items } = React.useContext(SelectContext)

    if (children !== undefined) {
      return <span ref={ref} className={className} style={{ pointerEvents: "none" }}>{children}</span>
    }

    const label = value != null
      ? (items.find(i => i.value === value)?.label ?? value)
      : null
    const showPlaceholder = label == null

    return (
      <span
        ref={ref}
        className={cn(showPlaceholder && "text-grey-600", className)}
        style={{ pointerEvents: "none" }}
      >
        {showPlaceholder ? placeholder : label}
      </span>
    )
  }
)
SelectValue.displayName = "SelectValue"

// ─── SelectTrigger ──────────────────────────────────────────────────────────

const SelectTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, children, ...props }, ref) => {
  const { open, setOpen, triggerRef, disabled } = React.useContext(SelectContext)

  const mergedRef = React.useCallback((node: HTMLButtonElement | null) => {
    (triggerRef as React.MutableRefObject<HTMLButtonElement | null>).current = node
    if (typeof ref === "function") ref(node)
    else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node
  }, [ref, triggerRef])

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if (disabled) return
    if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(e.key)) {
      e.preventDefault()
      setOpen(!open)
    }
  }, [disabled, open, setOpen])

  return (
    <button
      ref={mergedRef}
      type="button"
      role="combobox"
      aria-expanded={open}
      aria-haspopup="listbox"
      disabled={disabled}
      className={cn(
        "flex h-10 w-full items-center justify-between rounded-md border border-grey-200 bg-white px-3 py-2 text-sm text-grey-900 placeholder:text-grey-600 shadow-soft hover:border-grey-400 hover:shadow-hover focus:outline-none focus:border-grey-400 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 [&>span]:line-clamp-1",
        className
      )}
      onClick={() => { if (!disabled) setOpen(!open) }}
      onKeyDown={handleKeyDown}
      {...props}
    >
      {children}
      <ChevronDown className="h-4 w-4 shrink-0 text-grey-600 transition-colors" />
    </button>
  )
})
SelectTrigger.displayName = "SelectTrigger"

// ─── SelectContent ──────────────────────────────────────────────────────────

const SelectContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { position?: string }
>(({ className, children, position: _position, ...props }, ref) => {
  const { open, setOpen, triggerRef } = React.useContext(SelectContext)
  const contentRef = React.useRef<HTMLDivElement>(null)
  const [style, setStyle] = React.useState<React.CSSProperties>({})

  // Position dropdown below trigger
  React.useEffect(() => {
    if (!open || !triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    setStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      minWidth: rect.width,
      zIndex: 9999,
      // Override Radix Dialog's body pointer-events: none
      pointerEvents: "auto",
    })
  }, [open, triggerRef])

  // Close on click outside or Escape
  React.useEffect(() => {
    if (!open) return undefined
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node
      if (
        contentRef.current && !contentRef.current.contains(target) &&
        triggerRef.current && !triggerRef.current.contains(target)
      ) {
        setOpen(false)
      }
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener("pointerdown", handlePointerDown)
    document.addEventListener("keydown", handleKey)
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown)
      document.removeEventListener("keydown", handleKey)
    }
  }, [open, setOpen, triggerRef])

  // Portal to document.body with position: fixed for correct viewport positioning.
  // pointerEvents: "auto" overrides Radix Dialog's body-level pointer-events: none.
  return (
    <>
      {!open && <div style={{ display: "none" }}>{children}</div>}
      {open && createPortal(
        <div
          ref={(node) => {
            (contentRef as React.MutableRefObject<HTMLDivElement | null>).current = node
            if (typeof ref === "function") ref(node)
            else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node
          }}
          role="listbox"
          className={cn(
            "max-h-96 overflow-y-auto rounded-md border border-grey-200 bg-white text-grey-900 shadow-hover p-1 animate-in fade-in-0 zoom-in-95 slide-in-from-top-2",
            className
          )}
          style={style}
          {...props}
        >
          {children}
        </div>,
        document.body
      )}
    </>
  )
})
SelectContent.displayName = "SelectContent"

// ─── SelectItem ─────────────────────────────────────────────────────────────

interface SelectItemProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string
  disabled?: boolean
  textValue?: string
}

const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(
  ({ className, children, value, disabled = false, textValue, ...props }, ref) => {
    const { value: selectedValue, onSelect, registerItem } = React.useContext(SelectContext)
    const textRef = React.useRef<HTMLSpanElement>(null)
    const isSelected = selectedValue === value

    React.useEffect(() => {
      const label = textValue
        ?? (typeof children === "string" ? children : undefined)
        ?? textRef.current?.textContent?.trim()
        ?? undefined
      if (label) registerItem(value, label)
    }, [value, textValue, children, registerItem])

    return (
      <div
        ref={ref}
        role="option"
        aria-selected={isSelected}
        data-disabled={disabled || undefined}
        className={cn(
          "relative flex w-full cursor-pointer select-none items-center rounded-sm py-2 pl-8 pr-2 text-sm text-grey-900 outline-none hover:bg-grey-50 focus:bg-grey-50 focus:text-charcoal data-[disabled]:pointer-events-none data-[disabled]:opacity-50 transition-colors duration-150",
          isSelected && "bg-grey-50",
          className
        )}
        onClick={() => { if (!disabled) onSelect(value) }}
        {...props}
      >
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
          {isSelected && <Check className="h-4 w-4 text-charcoal" />}
        </span>
        <span ref={textRef}>{children}</span>
      </div>
    )
  }
)
SelectItem.displayName = "SelectItem"

// ─── SelectSeparator ────────────────────────────────────────────────────────

const SelectSeparator = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props}
  />
))
SelectSeparator.displayName = "SelectSeparator"

// ─── Compat stubs (API parity) ──────────────────────────────────────────────

const SelectGroup: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, ...props }) => (
  <div role="group" {...props}>{children}</div>
)
SelectGroup.displayName = "SelectGroup"

const SelectLabel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("py-1.5 pl-8 pr-2 text-sm font-medium", className)} {...props} />
  )
)
SelectLabel.displayName = "SelectLabel"

const SelectScrollUpButton = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  (_props, _ref) => null
)
SelectScrollUpButton.displayName = "SelectScrollUpButton"

const SelectScrollDownButton = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  (_props, _ref) => null
)
SelectScrollDownButton.displayName = "SelectScrollDownButton"

// ─── Exports ────────────────────────────────────────────────────────────────

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
